import {createAsyncThunk} from '@reduxjs/toolkit';
import deleteBlackout from 'api/blackouts/delete-blackout';
import getBlackouts from 'api/blackouts/get-blackouts';
import addInventoryException from 'api/inventory/add-exception';
import getDefaultInventory from 'api/inventory/get-default-inventory';
import getRuleDisplay from 'api/inventory/get-rule-display';
import removeException from 'api/inventory/remove-exception';
import updateDefaultInventory from 'api/inventory/update-default-inventory';
import updateMonthlyRentalRules from 'api/inventory/update-monthly-rental-rules';
import updateException from 'api/inventory/update-exception';
import getAllOperatorInventory from 'api/inventory/get-operator-inventory';

import {
    inventorySchema,
    monthlyInventorySchema,
} from 'pages/inventory/utils/validationSchemas';

/** @namespace Inventory.Actions */

/**
 * Thunk that gets the inventory exceptions for a facility.
 */
export const getInventoryExceptions = createAsyncThunk(
    'inventory/getInventoryExceptions',
    /**
     * @param {number} facilityId
     * @param dispatch
     * @param getState
     * @param rejectWithValue
     * @return {Promise<InventoryRule[]|Error>}
     */ async (facilityId, {rejectWithValue}) => {
        try {
            const response = await getRuleDisplay({id: facilityId});

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that adds an exception to the inventory exceptions for a facility or updates an existing exception if the id already exists.
 */
export const updateOrCreateInventoryException = createAsyncThunk(
    'inventory/updateOrCreateInventoryException',
    /**
     * @param {TInventoryRuleRequest} exception - The exception to add.
     * @param rejectWithValue
     * @return {Promise<TInventoryRule>}
     */
    async (exception, {rejectWithValue}) => {
        try {
            const response = await addInventoryException(exception);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that updates an inventory exception for a facility with a collection of given values.
 */
export const updateInventoryException = createAsyncThunk(
    'inventory/updateInventoryException',
    /**
     * @param {number} facilityId
     * @param {number} ruleId
     * @param {object} values
     * @param rejectWithValue
     * @return {Promise<TInventoryRule>}
     */
    async ({facilityId, ruleId, values}, {rejectWithValue}) => {
        try {
            const response = await updateException(facilityId, ruleId, values);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that deletes an inventory exception for a facility.
 */
export const deleteInventoryException = createAsyncThunk(
    'inventory/deleteInventoryException',
    /**
     *
     * @param {number} facilityId
     * @param {number} ruleId
     * @param rejectWithValue
     * @return {Promise<number|Error>} - The id of the deleted exception.
     */
    async ({facilityId, ruleId}, {rejectWithValue}) => {
        try {
            await removeException(facilityId, ruleId);

            return ruleId;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that gets the operator inventory for a facility. Includes both Transient and Monthly inventory.
 */
export const getOperatorInventory = createAsyncThunk(
    'inventory/getOperatorInventory',
    /**
     * @param {object} params
     * @param {number} params.facilityId
     * @param rejectWithValue
     * @return {Promise<TOperatorInventoryResponse|Error>}
     */
    async ({facilityId, type}, {rejectWithValue}) => {
        try {
            // Eventually, this will be responsible for getting both transient and monthly inventory, but for now, it's only monthly
            const response = await getAllOperatorInventory(facilityId);

            return {...response, type};
        } catch (err) {
            return rejectWithValue(err);
        }
    },
    {
        getPendingMeta: (params) => {
            return {type: params.arg.type};
        },
    }
);

// TODO: Remove after the new `operator-inventory` endpoint supports transient inventory
/**
 * Thunk that gets the default inventory for a facility.
 */
export const getBaseInventory = createAsyncThunk(
    'inventory/getBaseInventory',
    /**
     * @param {number} facilityId
     * @param rejectWithValue
     */
    async (facilityId, {rejectWithValue}) => {
        try {
            const response = await getDefaultInventory(facilityId);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that updates the default inventory for a facility.
 */
export const updateBaseInventory = createAsyncThunk(
    'inventory/updateBaseInventory',
    /**
     *
     * @param {number} facilityId
     * @param {string} spaces - The spaces to update
     * @param rejectWithValue
     * @return {Promise<{spaces: number}|Error>}
     */
    async ({facilityId, spaces}, {rejectWithValue}) => {
        try {
            // using a yup schema, validate updated inventory value
            const validatedInventory = await inventorySchema.validate(spaces);

            // post the updated inventory value to the API endpoint
            const response = await updateDefaultInventory({
                facilityId,
                spaces: validatedInventory,
            });

            return response;
        } catch (error) {
            // pass the error to the rejectWithValue function to allow the component to determine how to handle it
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that gets a collection of blackouts for a facility
 *
 */
export const getInventoryBlackouts = createAsyncThunk(
    'inventory/getInventoryBlackouts',
    /**
     * @param {number } facilityId
     * @param rejectWithValue
     */
    async (
        {page, dateFrom, dateTo, facilityId, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getBlackouts(
                {
                    page,
                    facilityIDs: facilityId,
                    dateFrom,
                    dateTo,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that deletes a blackout for a facility.
 */
export const deleteInventoryBlackout = createAsyncThunk(
    'inventory/deleteInventoryBlackout',
    /**
     *
     * @param {number} facilityId
     * @param {number} blackoutId
     * @param rejectWithValue
     * @return {Promise<number|Error>}
     */
    async ({facilityId, blackoutId}, {rejectWithValue}) => {
        try {
            await deleteBlackout(facilityId, blackoutId);

            return blackoutId;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that updates the default inventory for a facility.
 */
export const updateMonthlyInventory = createAsyncThunk(
    'inventory/updateMonthlyInventory',
    /**
     *
     * @param {object} params
     * @param {number} params.facilityId
     * @param {number} params.ruleId
     * @param {string} params.maxQuantity - The max quantity to update
     * @param {number} params.activeReservations - The number of active reservations
     * @param {string} params.type - The type of inventory to update
     * @param rejectWithValue
     * @return {Promise<{spaces: number}|Error>}
     */
    async (
        {facilityId, maxQuantity, ruleId, activeReservations, type},
        {rejectWithValue}
    ) => {
        try {
            // using a yup schema, validate updated inventory value
            const validatedInventory = await monthlyInventorySchema.validate(
                maxQuantity,
                {context: {activeReservations, type}}
            );

            // post the updated inventory value to the API endpoint
            const response = await updateMonthlyRentalRules({
                facilityId,
                ruleId,
                maxQuantity: validatedInventory,
            });

            return response;
        } catch (error) {
            // pass the error to the rejectWithValue function to allow the component to determine how to handle it
            return rejectWithValue(error);
        }
    }
);
